import {
  toggleSolutionTags,
  getBudget,
  updateSolutionBudget,
  getAllSolutionFilters,
  getSolutionStartEndValues,
  updateSolutionDates,
  updateSolutionTravelers,
  getTravelers,
  getStartEndDates,
  loadRecommendationDetails,
  getTripId,
  updateSolution,
  dropSolution,
  getAmexSolution,
  getSolutionContexts,
  getSolutionImg,
  dropManualActivities,
  setSolution,
} from '@/store/state/helpers';
import {computed} from '@vue/composition-api';
import {
  formatTimeFrameValue,
  FilterKind,
  BudgetFilterValue,
  getReadableDuration,
} from '@mobi-components/amex-bluesky-components';
import {modalFilterConfig, ModalKeys, ModalFilterConfig} from '../old-home-page/modal-filter.config';
import {VueConstructor} from 'vue/types/umd';
import {UserActivityCategoryEnum} from '@mobi-components/global-solution';
import {FilterTag} from '../../app-types';
import {ProblmSubmissionRoute} from '@/store/state/global.types';
import {useParams} from '@/router/router';
import MulticityModal from '@components/old-home-page/multi-city-trip/multicity-modal.vue';
import {adjustToDestinationTimezone} from '@/store/state/fix-solution-dates';
import {loadSolution} from '../old-home-page/load-solution';

export const TIME_FILTER_UPDATED = 'time-filter-updated';
export const TRAVELERS_FILTER_UPDATED = 'travelers-filter-updated';
export const TIME_FILTER_UPDATE_STARTED = 'time-filter-update-started';
export const TRAVELERS_FILTER_UPDATE_STARTED = 'travelers-filter-update-started';

export interface TimeFrameFilterValue {
  to: number;
  from: number;
}

interface FlightFilterOption {
  name: string;
  key: number;
  isActive: boolean;
}
export interface VueWithModal extends Vue {
  $modal: {
    show: (component: VueConstructor<Vue> | Vue, options: unknown) => Promise<void>;
    cancel: () => void;
  };
}

export function useSolutionFilters(root: VueWithModal, loadAdditionalExperiences: () => void = () => null) {
  const solution = computed(() => getAmexSolution());

  const solutionFilters = computed(() => {
    return getAllSolutionFilters();
  });

  const availableSolutionsFlightTags = computed(() => {
    return solutionFilters.value.filter((x) => x.domain === UserActivityCategoryEnum.TRANSPORTATION && x.name);
  });

  const solutionTimeFrameFilterValue = computed(() => {
    const {start, end} = getSolutionStartEndValues();
    const {start: defaultStart, end: defaultEnd} = getStartEndDates();
    return {
      from: start || defaultStart,
      to: end || defaultEnd,
    };
  });

  const timeFrameFilter = {
    label: 'Customize',
    get value() {
      const {start, end} = getSolutionStartEndValues();
      if (start && end) {
        return getReadableDuration(new Date(start), new Date(end));
      }

      return formatTimeFrameValue(solutionTimeFrameFilterValue.value);
    },
    type: FilterKind.TIME_FRAME,
  };

  interface DateRange {
    startDate: number;
    endDate: number;
  }

  const availableContexts = computed(() => {
    const params = useParams();
    const contextDates =
      solution.value?.activities.reduce((result: Record<string, DateRange>, x) => {
        // @ts-ignore
        const context = x.spec?.context || '';
        result[context] = result[context] || {startDate: 0, endDate: 0};

        if (!result[context].startDate || result[context].startDate > x.scheduled_start_time) {
          result[context].startDate = x.scheduled_start_time;
        }
        if (!result[context].endDate || result[context].endDate < x.scheduled_end_time) {
          result[context].endDate = x.scheduled_end_time;
        }
        return result;
      }, {}) || {};

    const notDuplicatedContexts = getSolutionContexts();
    return notDuplicatedContexts.map((x) => {
      return {
        name: x.name.replace('_', ', '),
        id: x.name,
        img: x.images.small,
        dateRange: contextDates[x.name || ''],
        isActive: params.value.contextName === x.name,
      };
    });
  });

  const allTrips = computed(() => [
    ...availableContexts.value.map((x) => ({
      ...x,
      dateRange: {
        startDate: new Date(x.dateRange.startDate * 1000),
        endDate: new Date(x.dateRange.endDate * 1000),
      },
    })),
  ]);

  const timeFrameConfig = {
    get filterValue() {
      return solutionTimeFrameFilterValue.value;
    },
    caption: `
      We'll find the very best flights, stays and things to do for your chosen dates.
      <br />
      Some of the previously selected activities may not fit after the date change. You can always adjust later!`,
    get activeSlot() {
      return true;
    },
    get availableTrips() {
      return allTrips.value;
    },
    get img() {
      return getSolutionImg();
    },
    onSelectionChange: async (contextsWithDates?: Record<string, Date>) => {
      root.$emit(TIME_FILTER_UPDATE_STARTED);

      // await updateSolutionTimeframe(value.from, value.to);

      if (!contextsWithDates) {
        root.$emit(TIME_FILTER_UPDATED);
        return;
      }

      const contexts = getSolutionContexts();
      const from = contextsWithDates['departure'].valueOf();
      let to = 0;

      const allContexts = [...contexts].map((x) => {
        const ms = adjustToDestinationTimezone(contextsWithDates[x.name].valueOf(), x.location.time_zone);
        const noon = Math.round((ms + 12 * 3600 * 1000) / 1000);
        if (ms >= to) {
          to = ms;
        }
        return {
          ...x,
          end_timestamp: noon,
        };
      });

      const tripId = getTripId();
      const oldSolution = await dropSolution();
      await dropManualActivities();
      const result = await loadSolution(tripId, [], ProblmSubmissionRoute.SOLUTION, [], allContexts);
      if (!result) {
        setSolution(oldSolution);
      } else {
        await updateSolutionDates(from, to);
      }
      root.$emit(TIME_FILTER_UPDATED);
    },
  };
  //#endregion

  //#region flights filter config

  const solutionFlightFilterValue = computed<FlightFilterOption[]>(() => {
    return availableSolutionsFlightTags.value.map((x, index) => ({
      name: x.name,
      key: index,
      isActive: x.value,
    }));
  });

  const flightFilter = computed(() => {
    return {
      label: 'Filter By',
      value: 'Best Flights',
      type: FilterKind.FLIGHT,
      isActive: availableSolutionsFlightTags.value.some((x) => x.value),
      counter: availableSolutionsFlightTags.value.filter((x) => x.value).length,
    };
  });

  const flightsConfig = {
    get availableTags() {
      return availableSolutionsFlightTags.value;
    },
    availableCategories: {
      TRANSPORTATION: true,
    },
    onSelectionChange: async (updatedTags: FilterTag[]) => {
      const off = availableSolutionsFlightTags.value.filter((x) => x.value).map((x) => ({...x, value: false}));
      await toggleSolutionTags([...off, ...updatedTags]);
    },
  };
  //#endregion

  //#region budget filter config
  const solutionBudget = computed(() => {
    return getBudget(ProblmSubmissionRoute.SOLUTION);
  });

  const solutionBudgetFilter = {
    label: 'Filter By',
    get value() {
      return 'Budget';
    },
    type: FilterKind.BUDGET,
  };

  const budgetConfig = {
    get filterValue() {
      return solutionBudget.value;
    },
    onSelectionChange: (value: BudgetFilterValue) => {
      return updateSolutionBudget(value);
    },
  };
  //#endregion

  //#region travelers filter config
  const solutionTravlersFilter = computed(() => {
    return getTravelers();
  });

  const travelersFilter = {
    label: 'Guests',
    get value() {
      const adults =
        solutionTravlersFilter.value.filter.adults === 'Add' ? '' : solutionTravlersFilter.value.filter.adults;
      const children =
        solutionTravlersFilter.value.filter.children === 'Add' ? '' : solutionTravlersFilter.value.filter.children;
      const rooms =
        solutionTravlersFilter.value.filter.rooms === 'Select' ? '' : solutionTravlersFilter.value.filter.rooms;
      return [adults, children, rooms].filter(Boolean).join(', ');
    },
    get adults() {
      return solutionTravlersFilter.value.filter.adults === 'Add'
        ? 0
        : parseInt(solutionTravlersFilter.value.filter.adults || '0', 10);
    },
    get children() {
      return solutionTravlersFilter.value.filter.children === 'Add'
        ? 0
        : parseInt(solutionTravlersFilter.value.filter.children || '0', 10);
    },

    type: FilterKind.TRAVELERS,
  };

  const travelersConfig = {
    get filterValue() {
      return solutionTravlersFilter.value;
    },
    onSelectionChange: async (value: {adults: string; children: string; rooms: string}) => {
      root.$emit(TRAVELERS_FILTER_UPDATE_STARTED);
      await updateSolutionTravelers(value);
      const newTripId = getTripId();
      await dropSolution();
      await updateSolution(newTripId, ProblmSubmissionRoute.SOLUTION, []);
      const params = useParams();
      loadRecommendationDetails(ProblmSubmissionRoute.SOLUTION, params.value.contextName);
      root.$emit(TRAVELERS_FILTER_UPDATED);
    },
  };
  //#endregion

  const allovedInterestDomains = [
    UserActivityCategoryEnum.ACCOMMODATION,
    UserActivityCategoryEnum.DINING,
    UserActivityCategoryEnum.EXPERIENCE,
  ];

  const availableSolutionsInterestTags = computed(() => {
    const y = solutionFilters.value.filter((x) => allovedInterestDomains.includes(x.domain));
    return y;
  });

  const interrestFilter = computed(() => {
    return {
      label: 'Filter By',
      value: 'Your Interests',
      type: FilterKind.INTERESTS,
      isActive: availableSolutionsInterestTags.value.some((x) => x.value),
      counter: availableSolutionsInterestTags.value.filter((x) => x.value).length,
    };
  });

  const interestsConfig = {
    get availableTags() {
      return availableSolutionsInterestTags.value;
    },
    onSelectionChange: (value: FilterTag[]) => {
      const off = availableSolutionsInterestTags.value.filter((x) => x.value).map((x) => ({...x, value: false}));
      return toggleSolutionTags([...off, ...value]);
    },
  };

  const onFilterClick = (filter: {type: FilterKind}) => {
    const timeframeOption: ModalFilterConfig | undefined =
      filter.type === FilterKind.TIME_FRAME
        ? {
            filter: FilterKind.TIME_FRAME,
            modal: MulticityModal,
            key: ModalKeys.TIMEFRAME_CONFIG,
          }
        : undefined;
    // @ts-ignore
    const modalForFilter: ModalFilterConfig | undefined =
      timeframeOption || modalFilterConfig.find((x) => x.filter === filter.type);
    if (modalForFilter) {
      const configs: Record<
        | ModalKeys.TIMEFRAME_CONFIG
        | ModalKeys.FLIGHTS_CONFIG
        | ModalKeys.BUDGET_CONFIG
        | ModalKeys.INTERESTS_CONFIG
        | ModalKeys.TRAVELERS_CONFIG,
        unknown
      > = {
        timeframeConfig: timeFrameConfig,
        travelersConfig: travelersConfig,
        flightsConfig: flightsConfig,
        budgetConfig: budgetConfig,
        interestsConfig: interestsConfig,
      };

      //@ts-ignore
      const config = configs[modalForFilter.key];
      root.$modal
        // @ts-ignore
        .show(modalForFilter.modal, config)
        .then(async (updatedTags) => {
          await config.onSelectionChange(updatedTags);
          return loadAdditionalExperiences && loadAdditionalExperiences();
        })
        .catch(() => null);
    }
  };

  return {
    timeFrameFilter,
    solutionFilters,
    travelersFilter,
    onFilterClick,
    flightFilter,
    solutionFlightFilterValue,
    solutionBudgetFilter,
    solutionBudget,
    interrestFilter,
    availableSolutionsFlightTags,
    availableSolutionsInterestTags,
    solutionTravlersFilter,
    solutionTimeFrameFilterValue,
  };
}
